import React from "react";
import { Box, Paper, Typography, Button, Chip, Tooltip, IconButton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { chipAprovacaoStatus } from "../../utils/aprovacao";

const botaoAcaoSx = {
  backgroundColor: "#1998a8",
  color: "#fff",
  borderRadius: "4px",
  mx: 0.25,
  width: "auto",
  "&:hover": { backgroundColor: "#1565c0" },
};

async function baixarBlobAnexo(url) {
  const tk = localStorage.getItem("token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${tk}` } });
  if (!res.ok) throw new Error("Erro ao buscar anexo.");
  return res.blob();
}

const fmtData = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
};

const STATUS_COR = {
  EmPreenchimento: { label: "Em Preenchimento", bg: "var(--ig-f-1e3a3a)", cor: "#1998a8" },
  EmAprovacao: { label: "Em Aprovação", bg: "var(--ig-f-1a2a3e)", cor: "#42a5f5" },
  Aprovada: { label: "Aprovada", bg: "var(--ig-f-1a3e2a)", cor: "#81c784" },
  Reprovada: { label: "Reprovada", bg: "var(--ig-f-3a1a1a)", cor: "#e57373" },
  AguardandoUpload: { label: "Em Preenchimento", bg: "var(--ig-f-1e3a3a)", cor: "#1998a8" },
};

/**
 * Painel de acompanhamento de um fluxo de aprovação (status/aprovadores, banner de
 * resultado, ações de Aprovar/Reprovar/Questionar/Responder/Revisar e histórico).
 * Reutilizado nos fluxos de Proposta, Fichas de Cadastro e Análise de Crédito.
 */
export default function PainelAprovacao({
  titulo,
  statusField,
  statusNaoIniciado = "EmPreenchimento",
  mostrarCabecalho = true,
  mostrarBotaoEnviar = true,
  aprovacao,
  autorizacoes = [],
  historicos = [],
  idUsuarioLogado,
  idCriador,
  revisarPorAlcada = false,
  revisarPorAlcadaOuCriador = false,
  usuarioEmAlcada = false,
  usuarioEhAprovador = false,
  reabrirProcessoBloqueado = false,
  onEnviar,
  enviando,
  onAprovar,
  onAbrirReprovar,
  onAbrirQuestionar,
  onAbrirResponder,
  onConfirmarRevisar,
  processando,
  podeEditar,
  pendencias = [],
  rotaAnexoBase,
}) {
  const handleVisualizarAnexo = async (h) => {
    // Abre a aba em branco JÁ na chamada síncrona do clique — se abrir só depois do
    // await abaixo, o navegador não reconhece como gesto do usuário e bloqueia o
    // pop-up silenciosamente (sem erro, sem nada visível).
    const janela = window.open("", "_blank");
    try {
      const blob = await baixarBlobAnexo(`${rotaAnexoBase}/${h.entidadeId}/aprovacao-historico/${h.id}/anexo`);
      const url = URL.createObjectURL(blob);
      if (janela) janela.location.href = url;
    } catch {
      if (janela) janela.close();
      // silencioso — mesmo padrão de erro de rede das outras ações deste painel
    }
  };

  const handleBaixarAnexo = async (h) => {
    try {
      const blob = await baixarBlobAnexo(`${rotaAnexoBase}/${h.entidadeId}/aprovacao-historico/${h.id}/anexo`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = h.nomeArquivoOriginal || "anexo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silencioso — mesmo padrão de erro de rede das outras ações deste painel
    }
  };

  const cfg = STATUS_COR[statusField] ?? { label: statusField ?? "—", bg: "var(--ig-f-252525)", cor: "#aaa" };

  // Para cada (idUsuario, ordem) mantém só a autorização de ID mais alto (ciclo atual)
  const autorizacoesAtivas = autorizacoes.filter((aut) => {
    const maxId = Math.max(...autorizacoes.filter((a) => a.idUsuario === aut.idUsuario && a.ordem === aut.ordem).map((a) => a.id));
    return aut.id === maxId;
  });
  const temCicloAtivo = historicos.some((h) => h.fluxo === "Ativo");
  const minhaAut = autorizacoesAtivas.find((a) => a.idUsuario === idUsuarioLogado);
  const podeAprovar = aprovacao?.status === "EmAnalise" && minhaAut?.status === "Aprovar";
  // Questão que designou este usuário para responder e que ele ainda não respondeu (mesmo
  // critério usado no backend para achar o histórico a atualizar em /responder). É a fonte
  // de verdade — o respondente pode ser qualquer usuário de alguma alçada, não só quem já
  // está na fila ordenada (AprovacaoAutorizacao) desta entidade.
  const minhaQuestao = historicos.find(
    (h) => h.fluxo === "Ativo" && h.status === "AguardandoResposta" && h.respondentes?.some((r) => r.idUsuario === idUsuarioLogado && !r.resposta),
  );
  const precisaResponder = !!minhaQuestao;
  // Critério de quem pode revisar: por padrão, só quem criou (ou sem criador definido).
  // `revisarPorAlcada` (hoje só Análise de Crédito) SUBSTITUI essa regra por "está
  // cadastrado em alguma alçada de aprovação". `revisarPorAlcadaOuCriador` (hoje só
  // Contrato) SOMA as duas: criador OU membro da alçada aplicável.
  const criterioCriador = idCriador === null || idCriador === idUsuarioLogado;
  const criterioRevisar = revisarPorAlcadaOuCriador ? usuarioEmAlcada || criterioCriador : revisarPorAlcada ? usuarioEmAlcada : criterioCriador;
  // Reprovado: o botão "Revisar" vira "Reabrir o Processo" — mesma ação (onConfirmarRevisar)
  // por enquanto; a ideia é esse botão ganhar mais ações por tipo de documento no futuro.
  const foiReprovado = aprovacao?.status === "Reprovado";
  // `reabrirProcessoBloqueado` (hoje só Proposta): quando reprovada, só pode reabrir se não
  // houver fluxo de aprovação em aberto em Fichas/Análise de Crédito — não afeta o "Revisar"
  // fora do caso reprovado (ex.: cancelar um ciclo próprio ainda EmAnalise).
  const podeRevisar =
    podeEditar &&
    statusField !== statusNaoIniciado &&
    !["Aprovada", "Aprovado"].includes(statusField) &&
    criterioRevisar &&
    !(foiReprovado && reabrirProcessoBloqueado);

  // Botão de Revisar/Reabrir: visível pra quem criou, mesmo que não esteja no fluxo de
  // aprovação (por isso fica fora do bloco só-pra-aprovadores, reaproveitado nos dois ramos abaixo).
  const botaoRevisar = podeRevisar && (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        variant="outlined"
        onClick={onConfirmarRevisar}
        disabled={processando}
        sx={{
          width: "auto",
          borderColor: "#e65100",
          color: "#e65100",
          "&:hover": { backgroundColor: "rgba(230,81,0,0.08)", borderColor: "#bf360c" },
          fontWeight: "bold",
          py: 1,
          px: 3,
        }}
      >
        {foiReprovado ? "Reabrir o Processo" : "↩ Revisar"}
      </Button>
    </Box>
  );

  const thSx = {
    backgroundColor: "var(--ig-f-252525)",
    color: "var(--ig-t-aaaaaa)",
    fontWeight: "bold",
    borderBottom: "1px solid var(--ig-b-444444)",
    py: 0.8,
    whiteSpace: "nowrap",
  };
  const tdSx = { borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-cccccc)", py: 0.8 };
  // Nunca deixa motivo/resposta passar de 2 linhas, em qualquer largura de tela — o
  // texto some com "..." e o Tooltip mostra o conteúdo completo ao passar o mouse.
  const clampSx = {
    maxWidth: 260,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  };

  /*
   * QUESTIONAMENTO NO HISTÓRICO
   * A linha do questionamento muda para "Respondido" no banco quando todos
   * respondem; aqui ela é exibida como "Questionou" (quem perguntou -> para quem),
   * e a linha da resposta como "Respondeu" (quem respondeu -> a quem).
   */
  const ehLinhaQuestionamento = (h) => Array.isArray(h.respondentes) && h.respondentes.length > 0;

  /*
   * NUMERAÇÃO: Q1, Q2... por ordem de criação; cada resposta recebe R<questão>.<ordem>
   * (ex.: R1.1, R1.2 quando vários respondentes respondem o mesmo questionamento).
   */
  const questionamentosOrdenados = historicos.filter(ehLinhaQuestionamento).sort((a, b) => a.id - b.id);

  const numeroQuestionamento = new Map(questionamentosOrdenados.map((q, i) => [q.id, i + 1]));

  const codigoResposta = new Map();

  questionamentosOrdenados.forEach((q) => {
    const numero = numeroQuestionamento.get(q.id);

    q.respondentes
      .filter((r) => r.resposta)
      .sort((a, b) => new Date(a.dataHoraResposta || 0) - new Date(b.dataHoraResposta || 0) || a.idUsuario - b.idUsuario)
      .forEach((r, i) => codigoResposta.set(`${q.id}-${r.idUsuario}`, `R${numero}.${i + 1}`));
  });

  const codigoQuestionamento = (q) => (numeroQuestionamento.has(q?.id) ? `Q${numeroQuestionamento.get(q.id)}` : "");

  const questionamentoDaResposta = (h) =>
    historicos.find((q) => ehLinhaQuestionamento(q) && q.respondentes.some((r) => r.idUsuario === h.idUsuario && (r.resposta || "") === (h.motivo || ""))) ||
    historicos.find((q) => ehLinhaQuestionamento(q) && q.id < h.id && q.respondentes.some((r) => r.idUsuario === h.idUsuario));

  const statusHistorico = (h) => {
    const detalhe = (texto, cor) => <Typography sx={{ fontSize: "0.72rem", color: cor, mt: 0.4, lineHeight: 1.25 }}>{texto}</Typography>;

    if (ehLinhaQuestionamento(h)) {
      const total = h.respondentes.length;
      const respondidos = h.respondentes.filter((r) => r.resposta).length;
      const nomes = h.respondentes
        .map((r) => r.usuario?.nome)
        .filter(Boolean)
        .join(", ");
      const situacao =
        respondidos === total ? (total > 1 ? `Respondido por todos (${respondidos}/${total})` : "Respondido") : `Aguardando resposta (${respondidos}/${total})`;
      return (
        <Box>
          <Chip label={`${codigoQuestionamento(h)} · Questionou`} size="small" sx={{ backgroundColor: "#7b1fa2", color: "#fff", fontWeight: "bold" }} />
          {nomes && detalhe(`para ${nomes}`, "var(--ig-t-cccccc)")}
          {detalhe(situacao, respondidos === total ? "var(--ig-t-81c784)" : "var(--ig-t-ffb74d)")}
        </Box>
      );
    }

    if (h.status === "Respondido") {
      const questionamento = questionamentoDaResposta(h);
      const codigo = questionamento ? codigoResposta.get(`${questionamento.id}-${h.idUsuario}`) : "";
      return (
        <Box>
          <Chip label={codigo ? `${codigo} · Respondeu` : "Respondeu"} size="small" sx={{ backgroundColor: "#e65100", color: "#fff", fontWeight: "bold" }} />
          {questionamento && detalhe(`ao ${codigoQuestionamento(questionamento)} de ${questionamento.usuario?.nome || "—"}`, "var(--ig-t-cccccc)")}
        </Box>
      );
    }

    return chipAprovacaoStatus(h.status);
  };

  const historicosDirecionadosAoUsuario = historicos.filter((h) => h.respondentes?.some((r) => r.idUsuario === idUsuarioLogado));

  /*
   * RESPONDENTE QUE NÃO É APROVADOR
   *
   * Todos os usuários veem o andamento do fluxo (aprovadores, quem já
   * aprovou e quem é o próximo). O conteúdo dos questionamentos aparece
   * só para aprovadores; quem foi questionado sem ser aprovador recebe,
   * acima do fluxo, o quadro com os questionamentos direcionados a ele
   * e o botão Responder.
   */
  const modoSomenteQuestionamento = !usuarioEhAprovador && historicosDirecionadosAoUsuario.length > 0;

  // Linhas de questionamento/resposta no histórico: só aprovadores veem. Quem foi
  // questionado (sem ser aprovador) lê e responde pelo quadro "Questionamento recebido".
  const historicosTabela = usuarioEhAprovador ? historicos : historicos.filter((h) => h.status !== "AguardandoResposta" && h.status !== "Respondido");

  const blocoQuestionamento = modoSomenteQuestionamento && (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: "var(--ig-f-1e1e1e)",
        borderRadius: 2,
        border: "1px solid var(--ig-b-333333)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
              fontSize: "0.95rem",
            }}
          >
            QUESTIONAMENTO RECEBIDO
          </Typography>

          {titulo && (
            <Typography
              sx={{
                color: "var(--ig-t-888888)",
                fontSize: "0.78rem",
                mt: 0.25,
              }}
            >
              {titulo}
            </Typography>
          )}
        </Box>

        {precisaResponder ? (
          <Chip
            label="AGUARDANDO RESPOSTA"
            size="small"
            sx={{
              backgroundColor: "rgba(230,81,0,0.18)",
              color: "var(--ig-t-ff8a65)",
              border: "1px solid #e65100",
              fontWeight: 600,
            }}
          />
        ) : (
          <Chip
            label="RESPONDIDO"
            size="small"
            sx={{
              backgroundColor: "rgba(46,125,50,0.18)",
              color: "var(--ig-t-81c784)",
              border: "1px solid #4caf50",
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      {historicosDirecionadosAoUsuario.length === 0 ? (
        <Box
          sx={{
            p: 2,
            border: "1px solid var(--ig-b-333333)",
            borderRadius: 1,
            backgroundColor: "var(--ig-f-252525)",
          }}
        >
          <Typography
            sx={{
              color: "var(--ig-t-888888)",
              fontSize: "0.85rem",
              fontStyle: "italic",
            }}
          >
            Nenhum questionamento direcionado a você.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {historicosDirecionadosAoUsuario.map((h) => {
            const meuRespondente = h.respondentes?.find((r) => r.idUsuario === idUsuarioLogado);

            const estaPendente = !meuRespondente?.resposta;

            return (
              <Box
                key={h.id}
                sx={{
                  p: 2,
                  backgroundColor: "var(--ig-f-252525)",
                  border: "1px solid var(--ig-b-333333)",
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "var(--ig-t-888888)",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      Questionado por
                    </Typography>

                    <Typography
                      sx={{
                        color: "var(--ig-t-ffffff)",
                        fontSize: "0.86rem",
                        mt: 0.25,
                      }}
                    >
                      {h.usuario?.nome ?? "—"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: "var(--ig-t-888888)",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      Data
                    </Typography>

                    <Typography
                      sx={{
                        color: "var(--ig-t-cccccc)",
                        fontSize: "0.86rem",
                        mt: 0.25,
                      }}
                    >
                      {fmtData(h.data)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      color: "var(--ig-t-888888)",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    {codigoQuestionamento(h) ? `Questionamento ${codigoQuestionamento(h)}` : "Questionamento"}
                  </Typography>

                  <Typography
                    sx={{
                      color: "var(--ig-t-ffffff)",
                      fontSize: "0.9rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {h.motivo || "—"}
                  </Typography>
                </Box>

                {h.nomeArquivoOriginal && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        color: "var(--ig-t-888888)",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Anexo do Questionamento
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "var(--ig-t-cccccc)",
                          fontSize: "0.82rem",
                          mr: 0.5,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {h.nomeArquivoOriginal}
                      </Typography>

                      <Tooltip title="Visualizar">
                        <IconButton size="small" sx={botaoAcaoSx} onClick={() => handleVisualizarAnexo(h)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Baixar">
                        <IconButton size="small" sx={botaoAcaoSx} onClick={() => handleBaixarAnexo(h)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    pt: 1.5,
                    borderTop: "1px solid var(--ig-b-333333)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "var(--ig-t-888888)",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    Sua Resposta
                  </Typography>

                  {meuRespondente?.resposta ? (
                    <>
                      <Typography
                        sx={{
                          color: "var(--ig-t-81c784)",
                          fontSize: "0.9rem",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {meuRespondente.resposta}
                      </Typography>

                      {meuRespondente.dataHoraResposta && (
                        <Typography
                          sx={{
                            color: "var(--ig-t-777777)",
                            fontSize: "0.72rem",
                            mt: 0.5,
                          }}
                        >
                          Respondido em {fmtData(meuRespondente.dataHoraResposta)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography
                      sx={{
                        color: "var(--ig-t-ffb74d)",
                        fontSize: "0.82rem",
                        fontStyle: "italic",
                      }}
                    >
                      Aguardando sua resposta.
                    </Typography>
                  )}
                </Box>

                {estaPendente && minhaQuestao?.id === h.id && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={() => onAbrirResponder?.(h.motivo ?? "")}
                      disabled={processando}
                      sx={{
                        width: "auto",
                        backgroundColor: "#e65100",
                        "&:hover": {
                          backgroundColor: "#bf360c",
                        },
                        fontWeight: "bold",
                        py: 1,
                        px: 3,
                      }}
                    >
                      RESPONDER
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );

  return (
    <>
      {blocoQuestionamento}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, border: "1px solid var(--ig-b-333333)" }}>
        {/* Cabeçalho */}
        {mostrarCabecalho && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 700, fontSize: "0.95rem" }}>{titulo}</Typography>
            <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.bg, color: cfg.cor, border: `1px solid ${cfg.cor}`, fontWeight: 600 }} />
          </Box>
        )}

        {/* Aprovação não iniciada */}
        {mostrarBotaoEnviar && (statusField === statusNaoIniciado || statusField === "AguardandoUpload") && podeEditar && (
          <Box sx={{ mb: 1 }}>
            {statusField !== "AguardandoUpload" && (
              <>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={onEnviar}
                  disabled={enviando || pendencias.length > 0}
                  sx={{
                    width: "auto",
                    backgroundColor: "#1998a8",
                    "&:hover": { backgroundColor: "#1565c0" },
                    "&.Mui-disabled": { backgroundColor: "var(--ig-f-333333)", color: "var(--ig-t-666666)" },
                    fontWeight: "bold",
                    py: 1,
                    px: 3,
                  }}
                >
                  {enviando ? "Enviando..." : `Enviar ${titulo} para Aprovação`}
                </Button>
                {pendencias.length > 0 && (
                  <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "var(--ig-f-2a1e1e)", border: "1px solid #d32f2f", borderRadius: 1, maxWidth: 560 }}>
                    <Typography sx={{ color: "var(--ig-t-ef9a9a)", fontSize: "0.82rem", fontWeight: 600, mb: 0.5 }}>
                      ⚠ Complete os itens abaixo antes de enviar para aprovação:
                    </Typography>
                    {pendencias.map((p, i) => (
                      <Typography key={i} sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.78rem", mt: 0.5 }}>
                        <strong style={{ color: "var(--ig-t-ffffff)" }}>{p.nome}:</strong> {p.pendencias.join(", ")}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {aprovacao && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Barra de status / aprovadores */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                p: 2,
                backgroundColor: "var(--ig-f-252525)",
                borderRadius: 1,
                border: "1px solid var(--ig-b-333333)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.85rem", fontWeight: 600 }}>Status:</Typography>
                {chipAprovacaoStatus(aprovacao?.status ?? "EmAnalise")}
              </Box>
              {temCicloAtivo &&
                autorizacoesAtivas.map((aut) => (
                  <Box key={aut.id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {aut.status === "Aprovado" && <CheckIcon sx={{ color: "#4caf50", fontSize: 16 }} />}
                    {aut.status === "Reprovado" && <CloseIcon sx={{ color: "#f44336", fontSize: 16 }} />}
                    {aut.status === "Responder" && <SendIcon sx={{ color: "#e65100", fontSize: 16 }} />}
                    {aut.status === "Revisao" && <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #1998a8", flexShrink: 0 }} />}
                    {aut.status === "Aprovar" && <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#1998a8", flexShrink: 0 }} />}
                    {aut.status === "Aguardando" && (
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", border: "1px solid var(--ig-b-555555)", flexShrink: 0 }} />
                    )}
                    {aut.status === "Questionar" && <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: "2px dashed #f57f17", flexShrink: 0 }} />}
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        color:
                          aut.status === "Aprovado"
                            ? "var(--ig-t-81c784)"
                            : aut.status === "Reprovado"
                              ? "#e57373"
                              : aut.status === "Responder"
                                ? "var(--ig-t-ff8a65)"
                                : aut.status === "Aprovar"
                                  ? "#1998a8"
                                  : aut.status === "Questionar"
                                    ? "var(--ig-t-ffb74d)"
                                    : "var(--ig-t-aaaaaa)",
                      }}
                    >
                      Aprovador {aut.ordem}: {aut.usuario?.nome ?? "—"}
                      {aut.dataAprovado ? ` em ${fmtData(aut.dataAprovado)}` : ""}
                    </Typography>
                  </Box>
                ))}
            </Box>

            {/* Banner final */}
            {aprovacao?.status === "Aprovado" && (
              <Box sx={{ p: 1.5, backgroundColor: "rgba(46,125,50,0.15)", border: "1px solid rgba(76,175,80,0.4)", borderRadius: 1 }}>
                <Typography sx={{ color: "var(--ig-t-81c784)", fontSize: "0.88rem", fontWeight: 500 }}>{titulo} aprovada por todos os aprovadores.</Typography>
              </Box>
            )}
            {aprovacao?.status === "Reprovado" && (
              <Box sx={{ p: 1.5, backgroundColor: "rgba(211,47,47,0.15)", border: "1px solid rgba(244,67,54,0.4)", borderRadius: 1 }}>
                <Typography sx={{ color: "#e57373", fontSize: "0.88rem", fontWeight: 500 }}>
                  {titulo} reprovada.
                  {usuarioEhAprovador && aprovacao.motivoReprovado ? ` Motivo: ${aprovacao.motivoReprovado}` : ""}
                </Typography>
              </Box>
            )}

            {/* Botões de ação para o usuário logado */}
            {podeAprovar && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={onAprovar}
                  disabled={processando}
                  sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 3 }}
                >
                  Aprovar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CloseIcon />}
                  onClick={onAbrirReprovar}
                  disabled={processando}
                  sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                >
                  Reprovar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={onAbrirQuestionar}
                  disabled={processando}
                  sx={{ width: "auto", backgroundColor: "#7b1fa2", "&:hover": { backgroundColor: "#6a1b9a" }, fontWeight: "bold", py: 1, px: 3 }}
                >
                  Questionar
                </Button>
              </Box>
            )}
            {precisaResponder && usuarioEhAprovador && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => onAbrirResponder(minhaQuestao?.motivo ?? "")}
                  disabled={processando}
                  sx={{ width: "auto", backgroundColor: "#e65100", "&:hover": { backgroundColor: "#bf360c" }, fontWeight: "bold", py: 1, px: 3 }}
                >
                  Responder
                </Button>
              </Box>
            )}
            {botaoRevisar}

            {/* Histórico */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 700, mb: 1.5, borderBottom: "1px solid var(--ig-b-2a2a2a)", pb: 0.5, letterSpacing: 0.5 }}
              >
                HISTÓRICO DE APROVAÇÕES
              </Typography>
              <TableContainer sx={{ backgroundColor: "transparent" }}>
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Ordem do Aprovador</TableCell>
                      <TableCell sx={thSx}>Usuário</TableCell>
                      <TableCell sx={thSx}>Data</TableCell>
                      <TableCell sx={thSx}>Status</TableCell>
                      <TableCell sx={thSx}>Fluxo</TableCell>
                      {usuarioEhAprovador && <TableCell sx={thSx}>Motivo</TableCell>}
                      {usuarioEhAprovador && <TableCell sx={thSx}>Respondido por</TableCell>}
                      {usuarioEhAprovador && <TableCell sx={thSx}>Resposta</TableCell>}
                      {usuarioEhAprovador && <TableCell sx={thSx}>Ações</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historicosTabela.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={usuarioEhAprovador ? 9 : 5}
                          sx={{ ...tdSx, textAlign: "center", fontStyle: "italic", color: "var(--ig-t-666666)", py: 3 }}
                        >
                          Nenhum registro de aprovação.
                        </TableCell>
                      </TableRow>
                    ) : (
                      historicosTabela.map((h) => {
                        const aut = autorizacoes.find((a) => a.idUsuario === h.idUsuario);
                        return (
                          <TableRow key={h.id} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                            <TableCell sx={tdSx}>
                              <Chip
                                label={aut ? String(aut.ordem).padStart(2, "0") : "—"}
                                size="small"
                                sx={{ backgroundColor: "#1998a8", color: "#fff", fontWeight: "bold", minWidth: 36 }}
                              />
                            </TableCell>
                            <TableCell sx={{ ...tdSx, color: "var(--ig-t-ffffff)" }}>{h.usuario?.nome ?? "—"}</TableCell>
                            <TableCell sx={tdSx}>{h.data ? `${fmtData(h.data)}h` : "—"}</TableCell>
                            <TableCell sx={tdSx}>{statusHistorico(h)}</TableCell>
                            <TableCell sx={tdSx}>
                              {titulo === "Contrato" && statusField === "Arquivado" ? (
                                h.status === "Arquivado" ? (
                                  <Chip
                                    label="Arquivado"
                                    size="small"
                                    sx={{
                                      backgroundColor: "#795548",
                                      color: "#fff",
                                      fontWeight: "bold",
                                    }}
                                  />
                                ) : h.fluxo === "Invalido" ? (
                                  <Chip
                                    label="Encerrado"
                                    size="small"
                                    sx={{
                                      backgroundColor: "#795548",
                                      color: "#fff",
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label="Ativo"
                                    size="small"
                                    sx={{
                                      backgroundColor: "#1998a8",
                                      color: "#fff",
                                      fontWeight: "bold",
                                    }}
                                  />
                                )
                              ) : h.fluxo === "Invalido" ? (
                                <Chip
                                  label="Inválido"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#555",
                                    color: "#999",
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="Ativo"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#1998a8",
                                    color: "#fff",
                                    fontWeight: "bold",
                                  }}
                                />
                              )}
                            </TableCell>
                            {usuarioEhAprovador && (
                              <TableCell sx={tdSx}>
                                {h.motivo ? (
                                  <Tooltip title={h.motivo}>
                                    <Typography component="div" sx={{ ...clampSx, fontSize: "0.82rem", color: "var(--ig-t-cccccc)" }}>
                                      {h.motivo}
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            )}
                            {usuarioEhAprovador && (
                              <TableCell sx={tdSx}>
                                {h.respondentes?.length
                                  ? h.respondentes.map((r) => (
                                      <Typography
                                        key={r.idUsuario}
                                        component="div"
                                        sx={{ fontSize: "0.82rem", color: r.resposta ? "var(--ig-t-ffb74d)" : "var(--ig-t-888888)" }}
                                      >
                                        {r.usuario?.nome ?? "—"}
                                        {r.resposta ? ` (${codigoResposta.get(`${h.id}-${r.idUsuario}`) || "respondeu"})` : " (aguardando)"}
                                      </Typography>
                                    ))
                                  : "—"}
                              </TableCell>
                            )}
                            {usuarioEhAprovador && (
                              <TableCell sx={tdSx}>
                                {h.respondentes?.some((r) => r.resposta)
                                  ? h.respondentes
                                      .filter((r) => r.resposta)
                                      .map((r) => (
                                        <Tooltip key={r.idUsuario} title={r.resposta}>
                                          <Typography component="div" sx={{ ...clampSx, fontSize: "0.82rem", color: "var(--ig-t-ff8a65)" }}>
                                            {codigoResposta.get(`${h.id}-${r.idUsuario}`) ? `${codigoResposta.get(`${h.id}-${r.idUsuario}`)} · ` : ""}
                                            {r.usuario?.nome}: {r.resposta}
                                          </Typography>
                                        </Tooltip>
                                      ))
                                  : "—"}
                              </TableCell>
                            )}
                            {usuarioEhAprovador && (
                              <TableCell sx={tdSx}>
                                {h.nomeArquivoOriginal ? (
                                  <>
                                    <Tooltip title="Visualizar">
                                      <IconButton size="small" sx={botaoAcaoSx} onClick={() => handleVisualizarAnexo(h)}>
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Baixar">
                                      <IconButton size="small" sx={botaoAcaoSx} onClick={() => handleBaixarAnexo(h)}>
                                        <DownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </Paper>
    </>
  );
}
